import { useEffect, useState } from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Col, Form } from 'antd';
import { Formik, FormikProps } from 'formik';
import { isEmpty, omit } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { doClearUpdateAccountSuitabilityInformation, doUpdateAccountSuitabilityInformation } from 'src/actions';
import {
  AccountFinancialInvestmentExperienceTypeDto,
  AccountInvestmentObjectivesDto,
  AccountLiquidityNeedsDto,
  AccountRiskToleranceDto,
  AccountTimeHorizonDto,
  ErrorResponseDto,
  UpdateAccountSuitabilityInformationBodyDto,
} from 'src/dtos';
import { MAlert, MFormRadioGroup, MFormSaveButton, MFormSelect, UnknownErrorMessage } from 'src/lib';
import { Account, AccountLiquidityNeedsLabel } from 'src/models';
import { Spacing } from 'src/styles';
import { assertNonNullable, Maybe } from 'src/utils';

import { InvestmentExperienceForm } from './InvestmentExperienceForm';
import { LiquidityNeedsTooltipContent } from './LiquidityNeedsTooltipContent';
import { INVESTMENT_OBJECTIVES_OPTION_LIST, TIME_HORIZON_OPTION_LIST } from './optionList';
import { upsertAccountHolderSuitabilityInformationValidation } from './validations';

const getRiskTolerance = (investmentObjective?: string): Maybe<AccountRiskToleranceDto> => {
  switch (investmentObjective) {
    case 'capitalPreservation':
      return AccountRiskToleranceDto.Low;
    case 'income':
      return AccountRiskToleranceDto.Low;
    case 'growth':
      return AccountRiskToleranceDto.High;
    case 'growthAndIncome':
      return AccountRiskToleranceDto.Medium;
    case 'speculation':
      return AccountRiskToleranceDto.High;
  }
};

export const LIQUIDITY_NEEDS_OPTION_LIST = [
  {
    value: AccountLiquidityNeedsDto.VeryImportant,
    label: AccountLiquidityNeedsLabel.VeryImportant,
    alert: 'In order to qualify for My IPO Offers your Liquidity Needs must be "Somewhat Important" or "Not Important"',
  },
  {
    value: AccountLiquidityNeedsDto.SomewhatImportant,
    label: AccountLiquidityNeedsLabel.SomewhatImportant,
  },
  {
    value: AccountLiquidityNeedsDto.NotImportant,
    label: AccountLiquidityNeedsLabel.NotImportant,
  },
];

export interface InvestmentExperienceView {
  stocks?: AccountFinancialInvestmentExperienceTypeDto & { checked: boolean };
  options?: AccountFinancialInvestmentExperienceTypeDto & { checked: boolean };
  commodities?: AccountFinancialInvestmentExperienceTypeDto & { checked: boolean };
  bonds?: AccountFinancialInvestmentExperienceTypeDto & { checked: boolean };
  margins?: AccountFinancialInvestmentExperienceTypeDto & { checked: boolean };
}

export interface SuitabilityInformationFormValues {
  liquidityNeeds?: AccountLiquidityNeedsDto;
  investmentObjectives?: AccountInvestmentObjectivesDto;
  timeHorizon?: AccountTimeHorizonDto;
  financialInvestmentExperience?: InvestmentExperienceView;
}

export interface SuitabilityInformationFormProps {
  account?: Account;
  onCancel?: () => void;
  onSave?: () => void;
}

export const SuitabilityInformationForm = ({ account, onCancel, onSave }: SuitabilityInformationFormProps) => {
  const dispatch = useDispatch();

  const isUpdateLoading = useSelector((state: any) => Boolean(state.accounts.updateSuitabilityInformation.isLoading));
  const succeededUpdate = useSelector((state: any) => state.accounts.updateSuitabilityInformation.__succeeded);
  const updateError: ErrorResponseDto | undefined = useSelector(
    (state: any) => state.accounts.updateSuitabilityInformation.error,
  );

  const [initialValues, setInitialValues] = useState<SuitabilityInformationFormValues>({});

  const isSuitabilityInformationAlreadySaved = () => !isEmpty(account?.suitabilityInformation);

  const _onCancel = (form: FormikProps<SuitabilityInformationFormValues>) => {
    form.resetForm();

    if (onCancel) {
      onCancel();
    }
  };

  const _onSave = (form: FormikProps<SuitabilityInformationFormValues>) => {
    form.submitForm();

    if (onSave) {
      onSave();
    }
  };

  useEffect(() => {
    setInitialValues({
      liquidityNeeds: account?.suitabilityInformation?.liquidityNeeds?.value,
      investmentObjectives: account?.suitabilityInformation?.investmentObjectives?.value,
      timeHorizon: account?.suitabilityInformation?.timeHorizon?.value,
      financialInvestmentExperience: {
        margins: account?.suitabilityInformation?.financialInvestmentExperience?.margins
          ? {
              tradesPerYear: account.suitabilityInformation.financialInvestmentExperience.margins.tradesPerYear.value,
              yearsOfExperience:
                account.suitabilityInformation.financialInvestmentExperience.margins.yearsOfExperience.value,
              checked: true,
            }
          : undefined,
        stocks: account?.suitabilityInformation?.financialInvestmentExperience?.stocks
          ? {
              tradesPerYear: account.suitabilityInformation.financialInvestmentExperience.stocks.tradesPerYear.value,
              yearsOfExperience:
                account.suitabilityInformation.financialInvestmentExperience.stocks.yearsOfExperience.value,
              checked: true,
            }
          : undefined,
        bonds: account?.suitabilityInformation?.financialInvestmentExperience?.bonds
          ? {
              tradesPerYear: account.suitabilityInformation.financialInvestmentExperience.bonds.tradesPerYear.value,
              yearsOfExperience:
                account.suitabilityInformation.financialInvestmentExperience.bonds.yearsOfExperience.value,
              checked: true,
            }
          : undefined,
        options: account?.suitabilityInformation?.financialInvestmentExperience?.options
          ? {
              tradesPerYear: account.suitabilityInformation.financialInvestmentExperience.options.tradesPerYear.value,
              yearsOfExperience:
                account.suitabilityInformation.financialInvestmentExperience.options.yearsOfExperience.value,
              checked: true,
            }
          : undefined,
        commodities: account?.suitabilityInformation?.financialInvestmentExperience?.commodities
          ? {
              tradesPerYear:
                account.suitabilityInformation.financialInvestmentExperience.commodities.tradesPerYear.value,
              yearsOfExperience:
                account.suitabilityInformation.financialInvestmentExperience.commodities.yearsOfExperience.value,
              checked: true,
            }
          : undefined,
      },
    });
  }, [account?.primaryAccountHolder]);

  useEffect(() => {
    if (succeededUpdate) {
      if (onCancel) {
        onCancel();
      }
    }
  }, [succeededUpdate]);

  useEffect(() => {
    return () => {
      dispatch(doClearUpdateAccountSuitabilityInformation());
    };
  }, []);

  return (
    <Formik
      enableReinitialize
      validateOnChange
      validateOnBlur
      onSubmit={values => {
        assertNonNullable(account, 'Account');
        const dto: UpdateAccountSuitabilityInformationBodyDto = {
          ...values,
          financialInvestmentExperience: isEmpty(values.financialInvestmentExperience)
            ? undefined
            : {
                margins: values.financialInvestmentExperience.margins?.checked
                  ? omit(values.financialInvestmentExperience.margins, 'checked')
                  : undefined,
                stocks: values.financialInvestmentExperience.stocks?.checked
                  ? omit(values.financialInvestmentExperience.stocks, 'checked')
                  : undefined,
                options: values.financialInvestmentExperience.options?.checked
                  ? omit(values.financialInvestmentExperience.options, 'checked')
                  : undefined,
                commodities: values.financialInvestmentExperience.commodities?.checked
                  ? omit(values.financialInvestmentExperience.commodities, 'checked')
                  : undefined,
                bonds: values.financialInvestmentExperience.bonds?.checked
                  ? omit(values.financialInvestmentExperience.bonds, 'checked')
                  : undefined,
              },
          riskTolerance: getRiskTolerance(values.investmentObjectives),
        };

        dispatch(
          doUpdateAccountSuitabilityInformation({
            params: {
              id: account.id,
            },
            body: dto,
          }),
        );
      }}
      initialValues={initialValues}
      validationSchema={upsertAccountHolderSuitabilityInformationValidation}>
      {form => {
        return (
          <Form autoComplete='off' style={{ width: '100%' }}>
            <Col span={24}>
              <MFormSelect
                label='Liquidity Needs'
                options={LIQUIDITY_NEEDS_OPTION_LIST}
                defaultValue={account?.suitabilityInformation?.liquidityNeeds.value}
                tooltip={{
                  type: 'modal',
                  title: 'Liquidity Needs',
                  content: <LiquidityNeedsTooltipContent />,
                  icon: 'info',
                }}
                error={form.errors.liquidityNeeds}
                alert={LIQUIDITY_NEEDS_OPTION_LIST.find(option => option.value === form.values.liquidityNeeds)?.alert}
                onChange={value => {
                  form.setFieldValue('liquidityNeeds', value);
                }}
                name='suitability-liquidity-needs-name-attribute'
                testId='liquidity-needs'
              />
            </Col>
            <Col span={24}>
              <MFormRadioGroup
                testId={'investment-objectives'}
                label='Investment Objectives'
                align='vertical'
                value={form.values.investmentObjectives}
                defaultValue={account?.suitabilityInformation?.investmentObjectives.value}
                options={INVESTMENT_OBJECTIVES_OPTION_LIST}
                onChange={value => {
                  form.setFieldValue('investmentObjectives', value);
                }}
                error={form.errors.investmentObjectives}
              />
            </Col>
            <Col span={24}>
              <MFormRadioGroup
                testId={'time-horizon'}
                label='Time Horizon'
                align='vertical'
                value={form.values.timeHorizon}
                defaultValue={account?.suitabilityInformation?.timeHorizon.value}
                options={TIME_HORIZON_OPTION_LIST}
                onChange={value => {
                  form.setFieldValue('timeHorizon', value);
                }}
                error={form.errors.timeHorizon}
              />
            </Col>
            <Col span={24}>
              <InvestmentExperienceForm form={form} />
            </Col>
            {updateError && (
              <Col span={24}>
                <MAlert
                  type='error'
                  description={<UnknownErrorMessage message={updateError?.message} />}
                  showIcon
                  icon={<ExclamationCircleOutlined />}
                  closable={false}
                  className={Spacing.my16}
                />
              </Col>
            )}
            <Col span={24}>
              <MFormSaveButton<SuitabilityInformationFormValues>
                loading={isUpdateLoading}
                onCancel={_onCancel}
                onSave={_onSave}
                isEditMode={isSuitabilityInformationAlreadySaved()}
                form={form}
                testId={'suitability-information'}
              />
            </Col>
          </Form>
        );
      }}
    </Formik>
  );
};
